import { makeAutoObservable } from 'mobx';

import { ApiError, CreateFeatureRequest, Feature, FeaturesService, PagedFeatures, UpdateFeatureRequest } from 'services/featuregating/generated';

export type { Feature, PagedFeatures };

export interface IFeatureStore
{
  pagedFeatures: PagedFeatures,
  feature?: Feature,
  isLoading: boolean,
  errorMessage: string,
  isError: boolean,
  findAsync: (query?: string, page?: number, pageSize?: number) => Promise<void>,
  requestSuggestionsAsync: (query?: string, limit?: number) => Promise<Feature[]>,
  getByTokenAsync: (token: string) => Promise<void>,
  createAsync: (name: string, description: string) => Promise<void>,
  updateAsync: (token: string, name: string, description: string) => Promise<void>,
  deleteAsync: (token: string) => Promise<void>,
  clearError: () => void,
}

export class FeatureStore implements IFeatureStore
{
  isLoading: boolean = false;
  private _errorMessage?: string = undefined;
  pagedFeatures: PagedFeatures = {};
  feature?: Feature = undefined;

  constructor()
  {
    makeAutoObservable(this);
  }

  get isError(): boolean
  {
    return this._errorMessage !== undefined;
  }
  get errorMessage(): string
  {
    return this._errorMessage || '';
  }

  async findAsync(query: string = "", page: number = 0, pageSize: number = 30)
  {
    await this.usingLoadingAsync
    (
      async () =>
      {
        const pagedFeatures = await FeaturesService.getFeatures({query: query, page: `${page}`, pageSize: `${pageSize}`});
        this.setPagedFeatures(pagedFeatures);
      },
    );
  }

  async requestSuggestionsAsync(query: string = "", limit: number = 30)
  {
    try
    {
      const pagedFeatures = await FeaturesService.getFeatures({query: query, page: '0', pageSize: `${limit}`});
      return pagedFeatures.data || [];
    }
    catch (e)
    {
      return [];
    }
  }

  async getByTokenAsync(token: string)
  {
    await this.usingLoadingAsync
    (
      async () =>
      {
        const feature = await FeaturesService.getFeatureByToken({token: token});
        this.setFeature(feature);
      },
      (error: Error) =>
      {
        const apiError = error as ApiError;
        if (apiError && apiError.status === 404)
        {
          this.setFeature(undefined);
        }
        else
        {
          this.setError(error);
        }
      },
    );
  }

  async createAsync(name: string, description: string)
  {
    const request: CreateFeatureRequest =
    {
      name: name,
      description: description,
    };
    await this.usingLoadingAsync
    (
      async () =>
      {
        const feature = await FeaturesService.postFeatures({requestBody: request});
        this.setFeature(feature);
        this.setPagedFeatures({});
      },
    );
  }

  async updateAsync(token: string, name: string, description: string)
  {
    const request: UpdateFeatureRequest =
    {
      name: name,
      description: description,
    };
    await this.usingLoadingAsync
    (
      async () =>
      {
        const feature = await FeaturesService.patchFeatureByToken({token: token, requestBody: request});
        this.setFeature(feature);
        this.setPagedFeatures({});
      },
    );
  }

  async deleteAsync(token: string)
  {
    await this.usingLoadingAsync
    (
      async () =>
      {
        await FeaturesService.deleteFeatureByToken({token: token});
        this.setPagedFeatures({});
      },
    );
  }

  private async usingLoadingAsync(actionAsync: () => Promise<void>, errorHandler?: (e: Error) => void)
  {
    this.clearError();
    this.setIsLoading(true);
    try
    {
      await actionAsync();
    }
    catch (e)
    {
      const error = e as Error;
      if (errorHandler)
      {
        errorHandler(error);
      }
      else
      {
        this.setError(error);
      }
    }
    finally
    {
      this.setIsLoading(false);
    }
  }
  private setPagedFeatures(pagedFeatures: PagedFeatures): void
  {
    this.pagedFeatures = pagedFeatures;
  }
  private setFeature(feature?: Feature): void
  {
    this.feature = feature;
  }
  private setIsLoading(isLoading: boolean): void
  {
    this.isLoading = isLoading;
  }
  private setError(error: Error): void
  {
    console.log(error);
    this._errorMessage = error.message;
  }
  clearError(): void
  {
    this._errorMessage = undefined;
  }
}